/**
 * Исполнитель очереди pending-операций (CC §4.9).
 *
 * Порядок повтора, терминальные ошибки и правила backoff — из §4.9.
 * Очередь обрабатывает ТОЛЬКО вкладка-лидер (CLIENT_WEB.md §9.2 п. 5).
 */
import { api, idempotency, unwrap } from '@shared/api/client'
import { ApiError, isNetworkError, isTerminal } from '@shared/api/errors'
import type { ErrorCode } from '@shared/api/types'
import { conversationsRepo, messagesRepo, MAX_ATTEMPTS, queueRepo, type PendingOp } from '@shared/db'
import { isLeaderTab, TAB_ID } from '@shared/ws/leader'

/** Пока replay не завершён, очередь не трогаем (CC §4.9 п. 2). */
let replayComplete = false
let running = false
let userId: string | null = null

export function setQueueContext(id: string | null): void {
  userId = id
}

export function setReplayComplete(value: boolean): void {
  replayComplete = value
}

/* ===================================================================== */
/* Выполнение одной операции                                              */
/* ===================================================================== */

/**
 * Исход выполнения операции.
 *
 * `defer` — операция ещё не может быть выполнена, но остаётся в очереди:
 * так ведёт себя сообщение, ждущее готовности вложения. Снимать его с
 * очереди нельзя, иначе после `media.ready` отправлять будет нечего.
 */
type ExecuteResult = 'done' | 'defer' | 'dropped'

async function execute(op: PendingOp): Promise<ExecuteResult> {
  switch (op.kind) {
    case 'receipts': {
      const item: Record<string, unknown> = { conversation_id: op.conversation_id }
      if (op.delivered_seq !== undefined) item['delivered_seq'] = op.delivered_seq
      if (op.read_seq !== undefined) item['read_seq'] = op.read_seq

      await api.POST('/receipts', { body: { items: [item] } as never })
      return 'done'
    }

    case 'send_message': {
      const local = await messagesRepo.byClientMessageId(op.client_message_id)
      if (!local) {
        // Пользователь удалил черновик — операция больше не нужна.
        await queueRepo.remove(op.op_id)
        return 'dropped'
      }

      // Сообщение с вложением ждёт `media.ready` (CC §5.3).
      if (local.local_state === 'pending_media') return 'defer'

      await messagesRepo.setLocalStateByClientId(op.client_message_id, { local_state: 'sending' })

      const body: Record<string, unknown> = {
        client_message_id: op.client_message_id,
        type: local.type,
      }
      if (local.text) body['text'] = local.text
      if (local.local_media?.media_id) body['media_id'] = local.local_media.media_id
      if (local.reply_to?.id) body['reply_to_id'] = local.reply_to.id
      if (op.forward_of) {
        // Контракт: у пересылки нет своего тела — текст, подпись и вложение копирует сервер,
        // а `type` обязан совпадать с исходным сообщением.
        body['forward_of'] = op.forward_of
        delete body['text']
        delete body['media_id']
      }

      /*
       * Вложение могло быть выбрано уже после постановки в очередь (C-11):
       * тип уточняется по реальному content_type подтверждённого медиа.
       */
      if (local.local_media?.media_id) {
        body['type'] = local.local_media.content_type?.startsWith('video')
          ? 'video'
          : local.local_media.content_type?.startsWith('audio')
            ? 'voice'
            : 'image'
      }

      const result = await api.POST('/conversations/{conversationId}/messages', {
        params: { path: { conversationId: op.conversation_id } },
        body: body as never,
        ...idempotency(op.idempotency_key ?? op.op_id),
      })

      const message = unwrap(result)
      const conversation = await conversationsRepo.get(op.conversation_id)
      await messagesRepo.confirmSent(
        op.client_message_id,
        message,
        userId ?? '',
        conversation?.counterpart_receipts,
      )
      return 'done'
    }

    case 'edit_message':
      await api.PATCH('/messages/{messageId}', {
        params: { path: { messageId: op.message_id } },
        body: { text: op.text } as never,
      })
      return 'done'

    case 'delete_message':
      await api.DELETE('/messages/{messageId}', {
        params: { path: { messageId: op.message_id }, query: { scope: op.scope } as never },
      })
      return 'done'

    case 'set_reaction':
      await api.PUT('/messages/{messageId}/reaction', {
        params: { path: { messageId: op.message_id } },
        body: { reaction: op.reaction } as never,
      })
      return 'done'

    case 'remove_reaction':
      await api.DELETE('/messages/{messageId}/reaction', {
        params: { path: { messageId: op.message_id } },
      })
      return 'done'

    case 'patch_conversation': {
      const body: Record<string, unknown> = {}
      if (op.patch.muted_until !== undefined) body['muted_until'] = op.patch.muted_until
      if (op.patch.archived !== undefined) body['archived'] = op.patch.archived

      const result = await api.PATCH('/conversations/{conversationId}', {
        params: { path: { conversationId: op.conversation_id } },
        body: body as never,
      })
      await conversationsRepo.put(unwrap(result))
      return 'done'
    }

    case 'pin_message': {
      const path = '/conversations/{conversationId}/pin' as const
      const params = { params: { path: { conversationId: op.conversation_id } } }
      const result = op.message_id
        ? await api.PUT(path, { ...params, body: { message_id: op.message_id } })
        : await api.DELETE(path, params)
      await conversationsRepo.put(unwrap(result))
      return 'done'
    }

    case 'match_seen':
      // 204 без тела: локально `seen` уже выставлен оптимистично (CC §4.9),
      // сервер подтвердит его событием `match.created` с новой версией.
      await api.POST('/matches/{matchId}/seen', {
        params: { path: { matchId: op.match_id } },
      })
      return 'done'

    case 'notifications_read':
      // Контракт: либо список `ids`, либо `all_before` — «всё до момента нажатия».
      // Пустой список означает «прочитать все» на момент постановки операции.
      await api.POST('/notifications/read', {
        body: op.notification_ids.length ? { ids: op.notification_ids } : { all_before: op.created_at },
      })
      return 'done'

    default:
      // Неизвестный вид операции держать в очереди бессмысленно.
      return 'done'
  }
}

/* ===================================================================== */
/* Обработка ошибок операции                                              */
/* ===================================================================== */

async function handleFailure(op: PendingOp, error: unknown): Promise<void> {
  const code: ErrorCode | null = error instanceof ApiError ? error.code : null
  const retryAfter = error instanceof ApiError ? error.retryAfter : null

  /*
   * CC §4.9 п. 5: 403 conversation_not_sendable снимает операцию с очереди,
   * сообщение переходит в `failed`, диалог перечитывается.
   * Автоматический повтор ЗАПРЕЩЁН.
   */
  if (code === 'conversation_not_sendable' || code === 'not_member') {
    await queueRepo.remove(op.op_id)

    if (op.kind === 'send_message') {
      await messagesRepo.setLocalStateByClientId(op.client_message_id, {
        local_state: 'failed',
        error_code: code,
      })
    }

    if ('conversation_id' in op) {
      try {
        const result = await api.GET('/conversations/{conversationId}', {
          params: { path: { conversationId: op.conversation_id } },
        })
        await conversationsRepo.put(unwrap(result))
      } catch {
        /* диалог мог исчезнуть целиком */
      }
    }
    return
  }

  // Сессия кончилась — операция ОСТАЁТСЯ в очереди (CC §4.9, §4.11).
  if (code === 'session_expired' || code === 'unauthenticated') {
    if (op.kind === 'send_message') {
      await messagesRepo.setLocalStateByClientId(op.client_message_id, { local_state: 'queued' })
    }
    await queueRepo.release(op.op_id)
    return
  }

  // Терминальные коды: повтор бессмысленен.
  if (isTerminal(error)) {
    await queueRepo.remove(op.op_id)
    if (op.kind === 'send_message') {
      await messagesRepo.setLocalStateByClientId(op.client_message_id, {
        local_state: 'failed',
        error_code: code,
      })
    }
    return
  }

  // Сеть, 503, 500, 429 — повтор с backoff.
  const updated = await queueRepo.recordFailure(op.op_id, code, retryAfter)

  if (op.kind === 'send_message') {
    const exhausted = (updated?.attempt_count ?? 0) >= MAX_ATTEMPTS
    await messagesRepo.setLocalStateByClientId(op.client_message_id, {
      local_state: exhausted ? 'failed' : 'queued',
      attempt_count: updated?.attempt_count ?? 0,
      next_attempt_at: updated?.next_attempt_at ?? null,
      error_code: code,
    })
  }

  if (!isNetworkError(error) && code === null) {
    // Непредвиденная ошибка — не зацикливаемся.
    await queueRepo.release(op.op_id)
  }
}

/* ===================================================================== */
/* Проход по очереди                                                      */
/* ===================================================================== */

/**
 * Один проход очереди. Безопасен при параллельных вызовах: повторный
 * вход отсекается флагом, а между вкладками — захватом операции.
 */
export async function runQueue(): Promise<void> {
  if (running || !replayComplete || !userId) return
  if (!isLeaderTab()) return

  running = true
  try {
    const ready = await queueRepo.ready()

    for (const op of ready) {
      // Отправки одного диалога идут строго последовательно (CC §4.9 п. 3).
      const claimed = await queueRepo.claim(op.op_id, TAB_ID)
      if (!claimed) continue

      try {
        const result = await execute(op)

        /*
         * `defer` оставляет операцию в очереди: сообщение ждёт готовности
         * вложения и уйдёт после `media.ready`. Снять её здесь значило бы
         * потерять сообщение молча — оно осталось бы в истории «в очереди»
         * навсегда.
         */
        if (result === 'done') await queueRepo.remove(op.op_id)
        else if (result === 'defer') await queueRepo.release(op.op_id)
      } catch (error) {
        await handleFailure(op, error)
        // Ошибка отправки останавливает очередь этого диалога до следующего прохода.
        if (op.kind === 'send_message') break
      }
    }
  } finally {
    running = false
  }
}

/* ===================================================================== */
/* Триггеры повтора (CLIENT_WEB.md §9.2 п. 5)                             */
/* ===================================================================== */

let intervalTimer: ReturnType<typeof setInterval> | null = null
let listenersAttached = false

export function startQueueRunner(): void {
  if (intervalTimer) clearInterval(intervalTimer)
  intervalTimer = setInterval(() => void runQueue(), 30_000)

  if (listenersAttached) return
  listenersAttached = true

  globalThis.addEventListener('online', () => void runQueue())
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void runQueue()
  })
}

export function stopQueueRunner(): void {
  if (intervalTimer) clearInterval(intervalTimer)
  intervalTimer = null
}
